import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Plus, Shield, X, Info, Vote, CheckCircle2, XCircle, Clock, Edit, Check } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Attendee {
  member_id?: string;
  member_name?: string;
  name?: string;
  full_name?: string;
}

interface ResolutionForAdoption {
  resolution_id: string;
  title: string;
  made_by: string;
  seconded_by: string;
  discussion_summary: string;
  vote_result: 'adopted' | 'rejected' | 'tabled' | 'withdrawn' | '';
  votes_for: number;
  votes_against: number;
  abstentions: number;
  adopted_at: string;
  resolution_number?: number | null;
}

interface ResolutionsSectionProps {
  data: {
    resolutions_noted: ResolutionForAdoption[];
    actual_attendees?: Attendee[];
  };
  onChange: (updates: any) => void;
  unaId: string;
  meetingId?: string;
  isFirstMeeting?: boolean;
}

const VOTE_RESULTS = [
  { value: 'adopted', label: 'Adopted', icon: CheckCircle2, color: 'text-green-600' },
  { value: 'rejected', label: 'Rejected', icon: XCircle, color: 'text-red-600' },
  { value: 'tabled', label: 'Tabled', icon: Clock, color: 'text-yellow-600' },
  { value: 'withdrawn', label: 'Withdrawn', icon: X, color: 'text-muted-foreground' },
];

export function ResolutionsSection({ data, onChange, unaId, meetingId, isFirstMeeting }: ResolutionsSectionProps) {
  const [selectedResolutionId, setSelectedResolutionId] = useState("");
  const [newResolution, setNewResolution] = useState<ResolutionForAdoption>({
    resolution_id: "",
    title: "",
    made_by: "",
    seconded_by: "",
    discussion_summary: "",
    vote_result: "",
    votes_for: 0,
    votes_against: 0,
    abstentions: 0,
    adopted_at: "",
  });

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editItem, setEditItem] = useState<Omit<ResolutionForAdoption, 'resolution_id' | 'title' | 'adopted_at'>>({
    made_by: "",
    seconded_by: "",
    discussion_summary: "",
    vote_result: "",
    votes_for: 0,
    votes_against: 0,
    abstentions: 0,
  });

  const startEditing = (index: number) => {
    const item = data.resolutions_noted[index];
    setEditItem({
      made_by: item.made_by || "",
      seconded_by: item.seconded_by || "",
      discussion_summary: item.discussion_summary || "",
      vote_result: item.vote_result || "",
      votes_for: item.votes_for || 0,
      votes_against: item.votes_against || 0,
      abstentions: item.abstentions || 0,
    });
    setEditingIndex(index);
  };

  const cancelEditing = () => {
    setEditingIndex(null);
  };

  const updateResolution = async (index: number) => {
    const existing = data.resolutions_noted[index];
    let resolutionNumber: number | null = existing.resolution_number ?? null;

    // Support Hub Stage 5, Part C: if edit transitions this entry to adopted and it has no
    // number yet, allocate a canon number and register it. Never re-allocate an existing number.
    if (
      editItem.vote_result === 'adopted' &&
      !resolutionNumber &&
      unaId
    ) {
      try {
        const { data: allocated, error: allocErr } = await supabase.rpc('allocate_resolution_number', {
          _una_id: unaId,
        });
        if (allocErr) throw allocErr;
        resolutionNumber = (allocated as number) ?? null;

        if (resolutionNumber) {
          const { data: userRes } = await supabase.auth.getUser();
          await supabase.from('resolution_registry').insert({
            una_id: unaId,
            resolution_number: resolutionNumber,
            title: existing.title,
            source: 'meeting_minutes',
            meeting_minutes_id: meetingId ?? null,
            adopted_date: new Date().toISOString().slice(0, 10),
            created_by: userRes?.user?.id ?? null,
          } as any);
        }
      } catch (err) {
        console.error('Failed to allocate resolution number on edit:', err);
      }
    }

    const updated = [...data.resolutions_noted];
    updated[index] = {
      ...updated[index],
      made_by: editItem.made_by,
      seconded_by: editItem.seconded_by,
      discussion_summary: editItem.discussion_summary,
      vote_result: editItem.vote_result as ResolutionForAdoption['vote_result'],
      votes_for: editItem.votes_for,
      votes_against: editItem.votes_against,
      abstentions: editItem.abstentions,
      resolution_number: resolutionNumber,
    };
    onChange({ resolutions_noted: updated });
    setEditingIndex(null);
  };

  // Fetch existing resolutions for this UNA, sorted by most recent first
  const { data: resolutions, isLoading } = useQuery({
    queryKey: ["una-resolutions", unaId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("una_resolutions")
        .select("id, resolution_title, resolution_category, resolution_date, created_at")
        .eq("una_id", unaId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!unaId,
  });

  // Get attendee name helper
  const getAttendeeName = (attendee: Attendee): string => {
    return attendee.member_name || attendee.name || attendee.full_name || 'Unknown';
  };

  const attendees = data.actual_attendees || [];

  const handleResolutionSelect = (resolutionId: string) => {
    setSelectedResolutionId(resolutionId);
    const selected = resolutions?.find(r => r.id === resolutionId);
    if (selected) {
      setNewResolution(prev => ({
        ...prev,
        resolution_id: selected.id,
        title: selected.resolution_title,
      }));
    }
  };

  const addResolution = async () => {
    if (!newResolution.resolution_id || !newResolution.title.trim() || !newResolution.vote_result) return;

    let resolutionNumber: number | null = null;

    // Support Hub Stage 5, Part C: allocate a canon number and register on adoption.
    if (newResolution.vote_result === 'adopted' && unaId) {
      try {
        const { data: allocated, error: allocErr } = await supabase.rpc('allocate_resolution_number', {
          _una_id: unaId,
        });
        if (allocErr) throw allocErr;
        resolutionNumber = (allocated as number) ?? null;

        if (resolutionNumber) {
          const { data: userRes } = await supabase.auth.getUser();
          await supabase.from('resolution_registry').insert({
            una_id: unaId,
            resolution_number: resolutionNumber,
            title: newResolution.title,
            source: 'meeting_minutes',
            meeting_minutes_id: meetingId ?? null,
            adopted_date: new Date().toISOString().slice(0, 10),
            created_by: userRes?.user?.id ?? null,
          } as any);
        }
      } catch (err) {
        console.error('Failed to allocate resolution number:', err);
      }
    }

    onChange({
      resolutions_noted: [...data.resolutions_noted, {
        ...newResolution,
        resolution_number: resolutionNumber,
        adopted_at: new Date().toISOString(),
      }],
    });

    setSelectedResolutionId("");
    setNewResolution({
      resolution_id: "",
      title: "",
      made_by: "",
      seconded_by: "",
      discussion_summary: "",
      vote_result: "",
      votes_for: 0,
      votes_against: 0,
      abstentions: 0,
      adopted_at: "",
    });
  };

  const removeResolution = (index: number) => {
    onChange({
      resolutions_noted: data.resolutions_noted.filter((_, i) => i !== index),
    });
  };

  // Filter out already-selected resolutions from the dropdown
  const alreadySelectedIds = data.resolutions_noted.map((r) => r.resolution_id);
  const availableResolutions = resolutions?.filter(r => !alreadySelectedIds.includes(r.id)) || [];

  const getVoteResultBadge = (result: string) => {
    const config = VOTE_RESULTS.find(v => v.value === result);
    if (!config) return null;
    const Icon = config.icon;
    return (
      <Badge variant={result === 'adopted' ? 'default' : result === 'rejected' ? 'destructive' : 'secondary'}>
        <Icon className={`h-3 w-3 mr-1 ${config.color}`} />
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-4">Resolutions for Adoption</h2>
        <p className="text-muted-foreground">
          Select resolutions to be voted on during this meeting. Record the motion, vote, and outcome.
        </p>
      </div>

      {/* First Meeting Helper Banner */}
      {isFirstMeeting && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            <strong>Don't worry about this section!</strong> All the necessary resolutions for your first meeting 
            were created and passed in the Motions section. This section is for linking existing resolutions 
            from your resolution library to future meetings - you can skip it for now.
          </AlertDescription>
        </Alert>
      )}

      {!isFirstMeeting && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            <strong>Tip:</strong> Resolutions should be created first using the "Writing a Resolution" workflow,
            then selected here for formal adoption during the board meeting.
          </AlertDescription>
        </Alert>
      )}

      {/* Add New Resolution for Adoption */}
      <Card className="p-4 space-y-4">
        <h3 className="font-semibold flex items-center gap-2">
          <Vote className="h-5 w-5" />
          Add Resolution for Vote
        </h3>

        <div className="grid gap-4">
          {/* Resolution Selection */}
          <div className="space-y-2">
            <Label htmlFor="resolution-select">Select Resolution *</Label>
            <Select
              value={selectedResolutionId}
              onValueChange={handleResolutionSelect}
            >
              <SelectTrigger id="resolution-select">
                <SelectValue placeholder={isLoading ? "Loading resolutions..." : "Select a resolution"} />
              </SelectTrigger>
              <SelectContent>
                {availableResolutions.length === 0 ? (
                  <div className="p-2 text-sm text-muted-foreground text-center">
                    {isLoading ? "Loading..." : "No resolutions available"}
                  </div>
                ) : (
                  availableResolutions.map((resolution) => (
                    <SelectItem key={resolution.id} value={resolution.id}>
                      <div className="flex flex-col">
                        <span>{resolution.resolution_title}</span>
                        {resolution.resolution_category && (
                          <span className="text-xs text-muted-foreground">
                            {resolution.resolution_category} • {resolution.resolution_date}
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {!isLoading && resolutions?.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No resolutions have been created yet. Create a resolution using the "Writing a Resolution" workflow first.
              </p>
            )}
          </div>

          {/* Motion Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="made-by">Motion Made By *</Label>
              <Select
                value={newResolution.made_by}
                onValueChange={(value) => setNewResolution(prev => ({ ...prev, made_by: value }))}
              >
                <SelectTrigger id="made-by">
                  <SelectValue placeholder="Select who made the motion" />
                </SelectTrigger>
                <SelectContent>
                  {attendees.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground text-center">
                      No attendees recorded yet
                    </div>
                  ) : (
                    attendees.map((attendee, idx) => (
                      <SelectItem key={attendee.member_id || idx} value={getAttendeeName(attendee)}>
                        {getAttendeeName(attendee)}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="seconded-by">Seconded By *</Label>
              <Select
                value={newResolution.seconded_by}
                onValueChange={(value) => setNewResolution(prev => ({ ...prev, seconded_by: value }))}
              >
                <SelectTrigger id="seconded-by">
                  <SelectValue placeholder="Select who seconded" />
                </SelectTrigger>
                <SelectContent>
                  {attendees.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground text-center">
                      No attendees recorded yet
                    </div>
                  ) : (
                    attendees.map((attendee, idx) => (
                      <SelectItem key={attendee.member_id || idx} value={getAttendeeName(attendee)}>
                        {getAttendeeName(attendee)}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Vote Result */}
          <div className="space-y-2">
            <Label htmlFor="vote-result">Vote Result *</Label>
            <Select
              value={newResolution.vote_result}
              onValueChange={(value) => setNewResolution(prev => ({ 
                ...prev, 
                vote_result: value as ResolutionForAdoption['vote_result'] 
              }))}
            >
              <SelectTrigger id="vote-result">
                <SelectValue placeholder="Select vote outcome" />
              </SelectTrigger>
              <SelectContent>
                {VOTE_RESULTS.map((result) => {
                  const Icon = result.icon;
                  return (
                    <SelectItem key={result.value} value={result.value}>
                      <div className="flex items-center gap-2">
                        <Icon className={`h-4 w-4 ${result.color}`} />
                        {result.label}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Vote Counts */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="votes-for">Votes For</Label>
              <Input
                id="votes-for"
                type="number"
                min="0"
                value={newResolution.votes_for}
                onChange={(e) => setNewResolution(prev => ({ ...prev, votes_for: parseInt(e.target.value) || 0 }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="votes-against">Votes Against</Label>
              <Input
                id="votes-against"
                type="number"
                min="0"
                value={newResolution.votes_against}
                onChange={(e) => setNewResolution(prev => ({ ...prev, votes_against: parseInt(e.target.value) || 0 }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="abstentions">Abstentions</Label>
              <Input
                id="abstentions"
                type="number"
                min="0"
                value={newResolution.abstentions}
                onChange={(e) => setNewResolution(prev => ({ ...prev, abstentions: parseInt(e.target.value) || 0 }))}
              />
            </div>
          </div>

          {/* Discussion Summary */}
          <div className="space-y-2">
            <Label htmlFor="discussion-summary">Discussion Summary (optional)</Label>
            <Textarea
              id="discussion-summary"
              value={newResolution.discussion_summary}
              onChange={(e) => setNewResolution(prev => ({ ...prev, discussion_summary: e.target.value }))}
              placeholder="Summarize any discussion that occurred before the vote..."
              rows={3}
            />
          </div>
        </div>

        <Button 
          onClick={addResolution} 
          disabled={!newResolution.resolution_id || !newResolution.title.trim() || !newResolution.vote_result || !newResolution.made_by || !newResolution.seconded_by}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Resolution Vote
        </Button>
      </Card>

      {/* Resolutions List */}
      {data.resolutions_noted.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-semibold">Resolutions Voted On ({data.resolutions_noted.length})</h3>

          {data.resolutions_noted.map((resolution, index: number) => (
            <Card key={index} className="p-4">
              {editingIndex === index ? (
                /* Edit Mode */
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Edit className="h-4 w-4 text-primary" />
                    <span className="font-semibold text-sm">Editing Resolution: {resolution.title}</span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor={`edit-made-by-${index}`}>Motion Made By *</Label>
                      <Select
                        value={editItem.made_by}
                        onValueChange={(value) => setEditItem(prev => ({ ...prev, made_by: value }))}
                      >
                        <SelectTrigger id={`edit-made-by-${index}`}>
                          <SelectValue placeholder="Select who made the motion" />
                        </SelectTrigger>
                        <SelectContent>
                          {attendees.length === 0 ? (
                            <div className="p-2 text-sm text-muted-foreground text-center">
                              No attendees recorded yet
                            </div>
                          ) : (
                            attendees.map((attendee, idx) => (
                              <SelectItem key={attendee.member_id || idx} value={getAttendeeName(attendee)}>
                                {getAttendeeName(attendee)}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`edit-seconded-by-${index}`}>Seconded By *</Label>
                      <Select
                        value={editItem.seconded_by}
                        onValueChange={(value) => setEditItem(prev => ({ ...prev, seconded_by: value }))}
                      >
                        <SelectTrigger id={`edit-seconded-by-${index}`}>
                          <SelectValue placeholder="Select who seconded" />
                        </SelectTrigger>
                        <SelectContent>
                          {attendees.length === 0 ? (
                            <div className="p-2 text-sm text-muted-foreground text-center">
                              No attendees recorded yet
                            </div>
                          ) : (
                            attendees.map((attendee, idx) => (
                              <SelectItem key={attendee.member_id || idx} value={getAttendeeName(attendee)}>
                                {getAttendeeName(attendee)}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`edit-vote-result-${index}`}>Vote Result *</Label>
                    <Select
                      value={editItem.vote_result}
                      onValueChange={(value) => setEditItem(prev => ({ 
                        ...prev, 
                        vote_result: value as ResolutionForAdoption['vote_result'] 
                      }))}
                    >
                      <SelectTrigger id={`edit-vote-result-${index}`}>
                        <SelectValue placeholder="Select vote outcome" />
                      </SelectTrigger>
                      <SelectContent>
                        {VOTE_RESULTS.map((result) => {
                          const Icon = result.icon;
                          return (
                            <SelectItem key={result.value} value={result.value}>
                              <div className="flex items-center gap-2">
                                <Icon className={`h-4 w-4 ${result.color}`} />
                                {result.label}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor={`edit-votes-for-${index}`}>Votes For</Label>
                      <Input
                        id={`edit-votes-for-${index}`}
                        type="number"
                        min="0"
                        value={editItem.votes_for}
                        onChange={(e) => setEditItem(prev => ({ ...prev, votes_for: parseInt(e.target.value) || 0 }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`edit-votes-against-${index}`}>Votes Against</Label>
                      <Input
                        id={`edit-votes-against-${index}`}
                        type="number"
                        min="0"
                        value={editItem.votes_against}
                        onChange={(e) => setEditItem(prev => ({ ...prev, votes_against: parseInt(e.target.value) || 0 }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`edit-abstentions-${index}`}>Abstentions</Label>
                      <Input
                        id={`edit-abstentions-${index}`}
                        type="number"
                        min="0"
                        value={editItem.abstentions}
                        onChange={(e) => setEditItem(prev => ({ ...prev, abstentions: parseInt(e.target.value) || 0 }))}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`edit-discussion-${index}`}>Discussion Summary</Label>
                    <Textarea
                      id={`edit-discussion-${index}`}
                      value={editItem.discussion_summary}
                      onChange={(e) => setEditItem(prev => ({ ...prev, discussion_summary: e.target.value }))}
                      placeholder="Summarize any discussion that occurred before the vote..."
                      rows={3}
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      onClick={() => updateResolution(index)} 
                      disabled={!editItem.made_by || !editItem.seconded_by || !editItem.vote_result}
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Save Changes
                    </Button>
                    <Button variant="outline" onClick={cancelEditing}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                /* Display Mode */
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold">{resolution.title}</h4>
                      {getVoteResultBadge(resolution.vote_result)}
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <p><span className="text-muted-foreground">Motion by:</span> {resolution.made_by}</p>
                      <p><span className="text-muted-foreground">Seconded by:</span> {resolution.seconded_by}</p>
                    </div>

                    <div className="flex gap-4 text-sm">
                      <span className="text-green-600">For: {resolution.votes_for}</span>
                      <span className="text-red-600">Against: {resolution.votes_against}</span>
                      <span className="text-muted-foreground">Abstentions: {resolution.abstentions}</span>
                    </div>

                    {resolution.discussion_summary && (
                      <p className="text-sm text-muted-foreground border-l-2 pl-3">
                        {resolution.discussion_summary}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => startEditing(index)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeResolution(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {data.resolutions_noted.length === 0 && (
        <Card className="p-8 text-center">
          <Shield className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
          <p className="text-muted-foreground">
            No resolutions added yet. Select resolutions to be voted on during this meeting.
          </p>
        </Card>
      )}
    </div>
  );
}
